import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield,
  Flag,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  User,
  FileText,
  Loader2,
  Eye,
  Ban,
  AlertOctagon,
  BarChart3
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * AdminModerationDashboard - Content Moderation Control Center
 * 
 * Features:
 * - Flagged content queue
 * - Moderation actions (Approved, Warning, Suspension, Ban)
 * - Full audit trail
 * - Statistics
 * 
 * Principles:
 * - AI assists, HUMANS enforce
 * - No shadow bans
 * - Transparent enforcement
 */
const AdminModerationDashboard = () => {
  const { user, authResolved } = useContext(AuthContext);
  // Auth token is stored in localStorage (not exposed via AuthContext).
  // Match the established pattern used by FounderConsole and FounderSponsorAdmin.
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('queue');
  const [loading, setLoading] = useState(true);

  // Check admin access — wait for auth to resolve to avoid race redirect.
  useEffect(() => {
    if (!authResolved) return;
    if (!user) {
      navigate('/auth?mode=signin');
      return;
    }
    if (!user.is_admin) {
      toast.error("Admin access required");
      navigate('/');
      return;
    }
    setLoading(false);
  }, [user, authResolved, navigate]);
  
  if (loading || !user?.is_admin) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold">Content Moderation</h1>
        </div>
        <p className="text-muted-foreground">
          Review flagged content and maintain community safety
        </p>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 mb-6">
          <TabsTrigger value="queue" className="flex items-center gap-2">
            <Flag className="h-4 w-4" />
            Review Queue
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
          <TabsTrigger value="stats" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Statistics
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="queue">
          <ModerationQueue token={token} />
        </TabsContent>
        
        <TabsContent value="audit">
          <AuditLog token={token} />
        </TabsContent>
        
        <TabsContent value="stats">
          <ModerationStats token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// --- MODERATION QUEUE ---
const ModerationQueue = ({ token }) => {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [actionDialog, setActionDialog] = useState({ open: false, flag: null });
  const [actionData, setActionData] = useState({ outcome: '', reason: '', suspension_days: '' });
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    fetchQueue();
  }, [categoryFilter]);
  
  const fetchQueue = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: 'pending' });
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      
      const res = await fetch(`${API_URL}/api/admin/moderation/queue?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFlags(data.flags);
      }
    } catch (error) {
      toast.error("Failed to load queue");
    } finally {
      setLoading(false);
    }
  };
  
  const handleAction = async () => {
    if (!actionData.outcome || !actionData.reason) {
      toast.error("Please select an outcome and provide a reason");
      return;
    }
    
    if (actionData.outcome === 'temporary_suspension' && !actionData.suspension_days) {
      toast.error("Please specify suspension duration");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/moderation/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          flag_id: actionDialog.flag.flag_id,
          outcome: actionData.outcome,
          reason: actionData.reason,
          suspension_days: actionData.outcome === 'temporary_suspension' ? parseInt(actionData.suspension_days) : null
        })
      });
      
      if (res.ok) {
        toast.success("Moderation action completed");
        setActionDialog({ open: false, flag: null });
        setActionData({ outcome: '', reason: '', suspension_days: '' });
        fetchQueue();
      } else {
        const error = await res.json();
        throw new Error(error.detail || "Failed to take action");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  const getCategoryBadge = (category) => {
    const colors = {
      'sexual_inappropriate': 'bg-red-100 text-red-700',
      'unsafe_for_children': 'bg-orange-100 text-orange-700',
      'harassment_abuse': 'bg-purple-100 text-purple-700',
      'hate_violence': 'bg-red-100 text-red-700',
      'spam_misleading': 'bg-yellow-100 text-yellow-700'
    };
    const labels = {
      'sexual_inappropriate': 'Sexual/Inappropriate',
      'unsafe_for_children': 'Unsafe for Children',
      'harassment_abuse': 'Harassment',
      'hate_violence': 'Hate/Violence',
      'spam_misleading': 'Spam'
    };
    return <Badge className={colors[category] || 'bg-gray-100'}>{labels[category] || category}</Badge>;
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-center">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="sexual_inappropriate">Sexual/Inappropriate</SelectItem>
                <SelectItem value="unsafe_for_children">Unsafe for Children</SelectItem>
                <SelectItem value="harassment_abuse">Harassment</SelectItem>
                <SelectItem value="hate_violence">Hate/Violence</SelectItem>
                <SelectItem value="spam_misleading">Spam</SelectItem>
              </SelectContent>
            </Select>
            
            <Badge variant="outline" className="h-10 px-4 flex items-center">
              <Flag className="h-4 w-4 mr-2" />
              {flags.length} pending
            </Badge>
          </div>
        </CardContent>
      </Card>
      
      {/* Flag List */}
      {flags.length === 0 ? (
        <Alert>
          <CheckCircle className="h-4 w-4 text-green-500" />
          <AlertDescription>No pending flags to review. Great job keeping the community safe!</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-3">
          {flags.map((flag) => (
            <Card key={flag.flag_id} className={flag.priority > 2 ? 'border-l-4 border-l-red-500' : ''}>
              <CardContent className="py-4">
                <div className="flex justify-between items-start">
                  <div className="space-y-2 flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {getCategoryBadge(flag.category)}
                      <Badge variant="outline">{flag.content_type}</Badge>
                      {flag.total_flags > 1 && (
                        <Badge variant="destructive">{flag.total_flags} flags</Badge>
                      )}
                      {flag.priority > 2 && (
                        <Badge className="bg-red-500">High Priority</Badge>
                      )}
                    </div>
                    
                    {/* Content Preview */}
                    <div className="bg-muted/50 rounded p-3 mt-2">
                      <p className="text-sm font-medium mb-1">Content Preview:</p>
                      <p className="text-sm text-muted-foreground">
                        {flag.content_preview?.caption || 
                         flag.content_preview?.text || 
                         flag.content_preview?.bio ||
                         flag.content_preview?.description ||
                         '(No text content)'}
                      </p>
                      {flag.content_preview?.has_photos && (
                        <Badge variant="secondary" className="mt-2">Contains Photos</Badge>
                      )}
                    </div>
                    
                    {/* Reporter & Owner Info */}
                    <div className="flex gap-4 text-xs text-muted-foreground mt-2">
                      <span><User className="h-3 w-3 inline mr-1" />Reported by: {flag.reporter_name}</span>
                      {flag.content_owner && (
                        <span>
                          <User className="h-3 w-3 inline mr-1" />
                          Owner: {flag.content_owner.name}
                          {flag.content_owner.is_business && ' (Business)'}
                        </span>
                      )}
                      <span><Clock className="h-3 w-3 inline mr-1" />{new Date(flag.created_at).toLocaleString()}</span>
                    </div>
                    
                    {/* Additional Reason */}
                    {flag.reason && (
                      <p className="text-xs text-muted-foreground mt-1">
                        <span className="font-medium">Reporter&apos;s note:</span> {flag.reason}
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="ml-4">
                    <Button onClick={() => setActionDialog({ open: true, flag })}>
                      <Eye className="h-4 w-4 mr-2" />
                      Review
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Action Dialog */}
      <Dialog open={actionDialog.open} onOpenChange={(open) => !open && setActionDialog({ open: false, flag: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Moderation Action
            </DialogTitle>
            <DialogDescription>
              Choose an outcome for this flagged content. All actions are logged.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Outcome Selection */}
            <div>
              <Label>Outcome</Label>
              <Select value={actionData.outcome} onValueChange={(v) => setActionData({...actionData, outcome: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select outcome..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      Approved (Content is OK)
                    </div>
                  </SelectItem>
                  <SelectItem value="removed_warning">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                      Remove + Warning
                    </div>
                  </SelectItem>
                  <SelectItem value="temporary_suspension">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-orange-500" />
                      Temporary Suspension
                    </div>
                  </SelectItem>
                  <SelectItem value="permanent_ban">
                    <div className="flex items-center gap-2">
                      <Ban className="h-4 w-4 text-red-500" />
                      Permanent Ban
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Suspension Duration */}
            {actionData.outcome === 'temporary_suspension' && (
              <div>
                <Label>Suspension Duration (days)</Label>
                <Input 
                  type="number" 
                  min="1" 
                  max="365"
                  value={actionData.suspension_days}
                  onChange={(e) => setActionData({...actionData, suspension_days: e.target.value})}
                  placeholder="e.g., 7"
                />
              </div>
            )}
            
            {/* Reason */}
            <div>
              <Label>Reason (Required)</Label>
              <Textarea 
                value={actionData.reason}
                onChange={(e) => setActionData({...actionData, reason: e.target.value})}
                placeholder="Explain the reason for this action..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be logged and may be shown to the user.
              </p>
            </div>
            
            {/* Warning for severe actions */}
            {(actionData.outcome === 'temporary_suspension' || actionData.outcome === 'permanent_ban') && (
              <Alert variant="destructive">
                <AlertOctagon className="h-4 w-4" />
                <AlertDescription>
                  {actionData.outcome === 'permanent_ban' 
                    ? 'Permanent bans cannot be undone. The user will be notified via email.'
                    : 'The user will be notified via email and unable to access their account during suspension.'}
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionDialog({ open: false, flag: null })}>
              Cancel
            </Button>
            <Button onClick={handleAction} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Confirm Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- AUDIT LOG ---
const AuditLog = ({ token }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState('all');
  
  useEffect(() => {
    fetchAudit();
  }, [outcomeFilter]);
  
  const fetchAudit = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (outcomeFilter && outcomeFilter !== 'all') params.append('outcome', outcomeFilter);
      
      const res = await fetch(`${API_URL}/api/admin/moderation/audit?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries);
      }
    } catch (error) {
      toast.error("Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };
  
  const getOutcomeBadge = (outcome) => {
    const config = {
      'approved': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'removed_warning': { color: 'bg-yellow-100 text-yellow-700', icon: AlertTriangle },
      'temporary_suspension': { color: 'bg-orange-100 text-orange-700', icon: Clock },
      'permanent_ban': { color: 'bg-red-100 text-red-700', icon: Ban }
    };
    const cfg = config[outcome] || { color: 'bg-gray-100', icon: FileText };
    const Icon = cfg.icon;
    return (
      <Badge className={cfg.color}>
        <Icon className="h-3 w-3 mr-1" />
        {outcome.replace('_', ' ')}
      </Badge>
    );
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  
  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Outcomes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Outcomes</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="removed_warning">Removed + Warning</SelectItem>
              <SelectItem value="temporary_suspension">Suspension</SelectItem>
              <SelectItem value="permanent_ban">Permanent Ban</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
      
      {/* Audit Entries */}
      <div className="space-y-2">
        {entries.map((entry) => (
          <Card key={entry.audit_id}>
            <CardContent className="py-3">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    {getOutcomeBadge(entry.outcome)}
                    <Badge variant="outline">{entry.content_type}</Badge>
                  </div>
                  <p className="text-sm">
                    <span className="font-medium">Admin:</span> {entry.admin_name}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Reason:</span> {entry.reason}
                  </p>
                  {entry.suspension_days && (
                    <p className="text-sm text-orange-600">
                      Suspended for {entry.suspension_days} days
                    </p>
                  )}
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>{new Date(entry.created_at).toLocaleString()}</p>
                  <p className="font-mono">{entry.audit_id}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {entries.length === 0 && (
          <Alert>
            <AlertDescription>No moderation actions found.</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};

// --- STATISTICS ---
const ModerationStats = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchStats();
  }, []);
  
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/moderation/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setStats(await res.json());
      }
    } catch (error) {
      toast.error("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  
  if (!stats) {
    return <Alert><AlertDescription>Unable to load statistics</AlertDescription></Alert>;
  }
  
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <Flag className="h-4 w-4" />
              Pending Flags
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{stats.pending_flags}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-1">
              <CheckCircle className="h-4 w-4" />
              Reviewed Flags
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.reviewed_flags}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* By Category */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pending by Category</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(stats.by_category).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(stats.by_category).map(([category, count]) => (
                <div key={category} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span className="text-sm">{category.replace('_', ' ')}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No pending flags</p>
          )}
        </CardContent>
      </Card>
      
      {/* Recent Outcomes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions This Week</CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(stats.recent_outcomes).length > 0 ? (
            <div className="space-y-2">
              {Object.entries(stats.recent_outcomes).map(([outcome, count]) => (
                <div key={outcome} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                  <span className="text-sm">{outcome.replace('_', ' ')}</span>
                  <Badge variant="outline">{count}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-4">No actions this week</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminModerationDashboard;
